import {
  dataFor,
  PICKER_GROUP,
  STATE_CHIP,
  validateCompositeForm,
  warnCompositeForm,
} from '@repo/ui';
import type {
  CompositeFormContext,
  CompositeFormErrors,
  CompositeFormWarnings,
  CompositeRefLabel,
  CompositeRowState,
  CompositeSource,
  CompositeSourceState,
  CompositeTargetKind,
  PickerCandidate,
} from '@repo/ui';
import type { ComboboxOption } from '@repo/design-system';
import {
  asArray,
  entityIdentityFor,
  floorEntityBid,
  isLive,
  layoutCurrentDesigns,
} from '@repo/front-end-utils';
import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';

import { compositeRefOptions, decodeRef, encodeRef, labelForRef } from './compositeRefOptions';
import {
  findContainedDoubleCounts,
  findCycle,
  findDoubleCounts,
  findDuplicateTarget,
  findUnmeasuredInputs,
  type GraphComposite,
} from './compositeGraph';
import {
  computeCompleteness,
  observerCountsFromLayout,
  planMeasures,
  type AutomaticTotal,
} from './entityCoverage';
import { buildContainment, type PlanContainment } from './planContainment';
import { automaticRowRefid } from './compositeEditing';
import { NAME_COLLATOR } from './inventorySort';

/**
 * **What is wrong with every composite measure on a plan** — the one answer the Composite measures
 * step, Launch and Go live all read (`composites-page-launch-refusals`, spec R1).
 *
 * WHY IT IS ITS OWN MODULE. The verdict pass used to live inside `OnboardingCompositeMeasures`,
 * where only the step could read it. Launch kept a duplicate-only check of its own, so a total the
 * step called "would be refused" for any other reason — a cycle, a double count, a reference the
 * plan lost — reached Go live unannounced and was refused there by the server. Two answers to one
 * question is the defect this whole epic exists to remove; one module is the fix.
 *
 * Pure: no React, no I/O, no store. Every rule it applies is the existing one, unchanged.
 */

/**
 * A picker option's entity BID, or `undefined` where the plan has not given it one.
 *
 * ⚠ **`meta` carries the BID — and, where there is none, the CATEGORY instead.**
 * `compositeRefOptions` sets it to `surface.entityCode || 'Zone'` / `item.entityCode || 'Equipment'`
 * and to `'Whole twin'` / `'Floor'` for the two non-entity kinds, and `entityCode` IS the full BID
 * (`FloorPlannerSurface.entityCode`, ruling R18). So the four fallbacks are the values that must
 * NOT be printed as identifiers: a warning reading `Zone 1 (Zone)` looks like it names something
 * and names nothing.
 *
 * ⚠ **A blank code is a real state**, not a defect — a placement the converter has not reconciled
 * yet. The parenthetical is omitted for it rather than filled in.
 */
const CATEGORY_FALLBACKS = ['Zone', 'Equipment', 'Whole twin', 'Floor'];
const entityCodeOf = (option: { meta?: string }): string | undefined => {
  const meta = (option.meta ?? '').trim();
  return meta !== '' && CATEGORY_FALLBACKS.indexOf(meta) < 0 ? meta : undefined;
};

/**
 * Each floor's plan name by id, trimmed; a floor with no usable name is left out. The one copy of
 * the floor-name walk — `filingTargetKey` keys a floor by it, and Launch names a floor line by it.
 */
export const floorNamesOf = (
  layout: FloorPlannerLayout | null | undefined,
): ReadonlyMap<number, string> => {
  const names = new Map<number, string>();
  for (const floor of asArray(layout?.floors)) {
    const name = typeof floor?.name === 'string' ? floor.name.trim() : '';
    if (typeof floor?.id === 'number' && name !== '') names.set(floor.id, name);
  }
  return names;
};

/**
 * The key `statement-file` compares composite targets on — so "one target" means what it means to
 * filing, not to the plan encoding (`launch-composite-refusals` R1; moved here from Launch by
 * `composites-page-launch-refusals`, spec R2).
 *
 * ⛔ **The plan's encoded reference is not the server's key, and the gap is where a gate misses.**
 * `buildConfigurationStatement` resolves a floor target to `{twinBid}-{floor name}` (trimmed) and an
 * entity target by its refid ALONE, and `statement-file` refuses `duplicate-target` on the
 * trim-upper-cased BID. So two floors whose names fold together ("Level 1", "LEVEL 1 "), a floor
 * named like an entity code ("S001") beside that entity, or an entity target whose stored row lost
 * its `entityKind`, are one target to the server and two — or none — to the plan encoding.
 *
 * - **Twin** → one key.
 * - **Floor** → `floorEntityBid`, the ONE copy of the floor rule the builder uses, upper-cased.
 *   `null` with no name or no twin BID: the builder refuses that build outright.
 * - **Entity** → the entity's stored identity (`entityIdentityFor`, the converter's own rule — "the
 *   stored identity IS the entity BID"), upper-cased. An entity with no identity yet is keyed by its
 *   refid, and that is a known gap rather than a match: Go Live mints it the next free code of
 *   its kind's letter (`Z###` / `S###` / `A###` for a space, `E###` for a machine), which is NOT
 *   predicted here (it depends on the register's reserved identities), so a floor named exactly like
 *   that next code — `Z001` beside the first blank-coded zone — is
 *   one target to filing and two here. `statement-file` still refuses it; this does not hold it in
 *   advance (Copilot review, PR #1843).
 */
export const filingTargetKey = (
  layout: FloorPlannerLayout | null | undefined,
  twinBid: string | null | undefined,
): ((ref: FloorPlannerCompositeRef | null | undefined) => string | null) => {
  const floorNames = floorNamesOf(layout);
  const identities = new Map<string, string>();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const entity of [...asArray(design?.surfaces), ...asArray(design?.items)]) {
      if (!entity || entity._deleted || typeof entity.refid !== 'string') continue;
      const identity = entityIdentityFor(twinBid, entity.entityCode);
      if (identity) identities.set(entity.refid, identity);
    }
  }

  return ref => {
    if (ref == null || typeof ref !== 'object') return null;
    if (ref.kind === 'twin') return 'twin';
    if (ref.kind === 'floor') {
      const bid = floorEntityBid(twinBid, floorNames.get(ref.floorId));
      return bid ? bid.toUpperCase() : null;
    }
    if (ref.kind === 'entity') {
      if (typeof ref.refid !== 'string' || ref.refid === '') return null;
      return (
        identities.get(ref.refid) ?? `refid${String.fromCharCode(31)}${ref.refid}`
      ).toUpperCase();
    }
    return null;
  };
};

/** A stored measure with its references resolved to how they READ. */
export interface LabelledComposite {
  refid: string;
  target: CompositeRefLabel;
  inputs: CompositeRefLabel[];
  includesOwnObservers?: boolean;
  excluded?: boolean;
  generated?: true;
}

/** Everything the rules need about one plan, built once per plan change. */
export interface CompositeVerdictContext {
  /** The picker's options — what the plan still holds, in reading order. */
  options: ComboboxOption[];
  /** Live observers per encoded reference, for `target-collision` and completeness. */
  observerCounts: ReadonlyMap<string, number>;
  /** What sits inside what — the half of the double-count question a statement cannot express. */
  containment: PlanContainment;
  /** The measures that are not tombstoned. */
  live: FloorPlannerComposite[];
  /** The plan's measures as edges (`planMeasures`). */
  others: GraphComposite[];
  /** Every live measure's references resolved to how they read. */
  labelled: LabelledComposite[];
  /** The references a person may actually pick. */
  pickable: ReadonlySet<string>;
  /** Each reference's entity BID, where it genuinely has one. */
  codes: ReadonlyMap<string, string>;
  /** Option value → label, built ONCE per plan change. */
  labels: ReadonlyMap<string, string>;
  /** The same resolution the stage's own warnings use, handed to the editor for its. */
  codeForRef: (value: string) => string | undefined;
  /** Every verdict that needs the OTHER measures on the plan, for one candidate. */
  graphOf: (candidate: GraphComposite) => NonNullable<CompositeFormContext['graph']>;
  /**
   * Are two encoded targets ONE target — to the plan encoding or to filing's key? The union the
   * duplicate rule uses, exposed so the editor matches "already totalled" the same way
   * (`composites-page-editor`, spec R13).
   */
  sameTarget: (a: string, b: string) => boolean;
}

/**
 * The candidate as the rules must see it, from a target and a raw input list.
 *
 * ⚠ **One shaping site, for the same reason there is one verdict site.** The editor filtered its
 * blank rows out before asking; the list handed the raw array over. `encodeRef` returns the EMPTY
 * STRING for a null or malformed entry — its own comment says it exists to do that rather than
 * throw inside a render memo — so a plan carrying one made the two disagree about the same
 * measure, which is precisely what this feature exists to prevent. The list marked the total,
 * counted it in "N would be refused", and rendered a sentence with the names missing; the editor
 * showed nothing. Found by review, 2026-09-19, and confirmed against the real fixture.
 *
 * `null` where there is nothing to judge: the rules need a target.
 */
export const candidateOf = (
  refid: string,
  target: string | null,
  inputs: string[],
): GraphComposite | null =>
  target === null || target === ''
    ? null
    : { refid, target, inputs: inputs.filter(value => value !== '') };

/**
 * Build everything the rules need about one plan.
 *
 * `twinName` is exactly what the step passes to `compositeRefOptions` — the twin record's name — so
 * a sentence naming the whole twin reads the same on every surface that asks.
 */
export const compositeVerdictContext = ({
  composites,
  layout,
  twinBid,
  twinName,
}: {
  /** The plan's complex measures, tombstones included. */
  composites: FloorPlannerComposite[] | null | undefined;
  layout: FloorPlannerLayout | null | undefined;
  twinBid: string | null | undefined;
  twinName?: string;
}): CompositeVerdictContext => {
  const options = compositeRefOptions(layout, twinBid ?? undefined, twinName);

  /*
   * How many live observers the plan holds against each entity, keyed by the same encoded
   * reference the picker uses.
   *
   * Filing DERIVES a partial group for any entity with more than one observer, so a measure
   * targeting such an entity has to be the one counting them — `target-collision`.
   */
  const observerCounts = observerCountsFromLayout(layout ?? null);

  /*
   * What the plan says about which things sit inside which — the half of the double-count question
   * a configuration statement cannot express.
   *
   * Built once per plan change and memoised per pair inside, never as a full pairwise product:
   * on an adopted gym that would be 43 surfaces × 160 entities of work nobody asked for.
   */
  const containment = buildContainment(layout ?? null, observerCounts);

  /** What the operator sees: the measures that are not tombstoned. */
  const live = asArray(composites).filter(isLive);

  /*
   * The plan's measures as edges — built once and read by both the list pass and the editor.
   *
   * `planMeasures`, not a hand-rolled map: it applies `isLive`, encodes, carries `excluded`, and
   * DROPS a measure whose target cannot be encoded. That last one matters — a hand-rolled map gives
   * an unencodable target the empty string, two malformed rows then collide on one key, and an
   * input that also fails to encode reads as totalled rather than uncovered, so the editor silently
   * stops warning about it.
   */
  const others = planMeasures(live);

  /*
   * Every measure's references resolved to how they READ — before any verdict is formed about them.
   *
   * ⚠ **Separate from the verdicts so they can see it.** The warnings name a reference the plan no
   * longer holds, and `labelForRef` is what turns that into "no longer on the plan" rather than a
   * raw encoded value carrying a U+001F separator.
   */
  const labelled: LabelledComposite[] = live.map(c => ({
    refid: c.refid,
    target: labelForRef(c.target, options),
    inputs: asArray(c.inputs).map(i => labelForRef(i, options)),
    includesOwnObservers: c.includesOwnObservers,
    excluded: c.excluded,
    ...(c.generated === true ? { generated: true as const } : {}),
  }));

  /*
   * `pickable` is `options` and never the label map — the two answer opposite questions, and
   * deriving one from the other makes a dangling reference count as on the plan and silently
   * turns its refusal off. The editor says so at length; this is the same construction.
   */
  const pickable = new Set(options.map(o => o.value));

  /*
   * Each reference's entity BID, so a warning can name the missing thing by name AND identifier.
   * `entityCode` IS the full BID (`FloorPlannerSurface.entityCode`, ruling R18), and the option's
   * `meta` falls back to the CATEGORY where a placement has not been reconciled — so the fallback
   * is filtered out rather than printed as though it were an identifier.
   */
  const codes = new Map<string, string>();
  for (const option of options) {
    const code = entityCodeOf(option);
    if (code) codes.set(option.value, code);
  }

  const labels = new Map(options.map(o => [o.value, o.label] as const));

  const codeForRef = (value: string): string | undefined => codes.get(value);

  /*
   * `duplicate-target`, matched the way FILING matches it (spec R2; ruled by maria@twinlabs.ai,
   * 2026-10-04). Two targets are one if the plan encodes them alike OR filing keys them alike — the
   * union, so the step never catches fewer duplicates than the plain comparison did. Keys are cached
   * per encoded value: the comparison runs once per pair of measures.
   */
  const filingKey = filingTargetKey(layout, twinBid);
  const keys = new Map<string, string | null>();
  const keyOf = (value: string): string | null => {
    if (!keys.has(value)) keys.set(value, filingKey(decodeRef(value)));
    return keys.get(value) ?? null;
  };
  const sameTarget = (a: string, b: string): boolean => {
    if (a === b) return true;
    const ka = keyOf(a);
    return ka !== null && ka === keyOf(b);
  };

  /**
   * Every verdict that can only be answered by looking at the OTHER measures on the plan, for one
   * candidate.
   *
   * ⚠ **One function, every caller** (`complex-measures-in-configuration-composites-stage`, its FR7). The editor asks it about the
   * measure in the form; the list and Launch ask it about every measure the plan holds. If any of
   * them had its own check, they could disagree about the same measure — which is the defect this
   * feature exists to prevent, one level up.
   */

  const graphOf = (candidate: GraphComposite) => {
    const cycleThrough = findCycle(candidate, others) ?? undefined;
    const doubleCount = findDoubleCounts(candidate, others) ?? undefined;
    const unmeasured = findUnmeasuredInputs(candidate, others, observerCounts);
    /*
     * The propagated case — an input that IS totalled, by a total that is itself unfinished.
     * Read straight from the shared recursive rule rather than through `findUnmeasuredInputs`,
     * which deliberately returns only the no-source half: this one must never reach the Save
     * gate, because the problem is in a measure the operator is not editing.
     */
    const unsatisfiedHere =
      computeCompleteness({
        observerCountsByRef: observerCounts,
        measures: [...others.filter(o => o.refid !== candidate.refid), candidate],
      }).get(candidate.refid)?.unsatisfied ?? [];
    const unfinishedTotals = unsatisfiedHere
      .filter(entry => entry.reason === 'unfinished-total')
      .map(entry => entry.ref);
    const excludedTotals = unsatisfiedHere
      .filter(entry => entry.reason === 'excluded-total')
      .map(entry => entry.ref);
    /*
     * Runs whether or not `findDoubleCounts` found something, and never re-reports its pair. A
     * measure can carry both kinds of double count — the whole-twin total on staging plan 51
     * does — and saying only the first would leave the biggest total on the gym silent about half
     * of what is wrong with it. Rob's ruling, 2026-09-19.
     */
    const { contained, undecided } = findContainedDoubleCounts(candidate, others, containment);
    return {
      ...(cycleThrough ? { cycleThrough } : {}),
      ...(doubleCount ? { doubleCount } : {}),
      ...(unmeasured.length > 0 ? { unmeasured } : {}),
      ...(unfinishedTotals.length > 0 ? { unfinishedTotals } : {}),
      ...(excludedTotals.length > 0 ? { excludedTotals } : {}),
      ...(contained ? { containedInput: contained } : {}),
      ...(undecided ? { undecidedContainment: undecided } : {}),
      duplicateTarget: findDuplicateTarget(candidate, others, sameTarget),
    };
  };

  return {
    options,
    observerCounts,
    containment,
    live,
    others,
    labelled,
    pickable,
    codes,
    labels,
    codeForRef,
    graphOf,
    sameTarget,
  };
};

/**
 * Everything wrong with ONE stored total, index-aligned with its inputs — the answer the list row,
 * the editor panel and Launch all read (`composites-page-editor`, spec R22).
 *
 * ⚠ **The same `validateCompositeForm` the rules suites pin, over the stored total.** The list used
 * to call it here and the editor called it itself; two call sites are how the two came to disagree
 * about one measure. The panel needs the answer per INPUT (each source card carries its own
 * sentences), so the shape stays index-aligned and `planCompositeVerdicts` flattens it.
 *
 * - `errors` / `warnings` — the rules' own output.
 * - `targetNotes` — *"Named for X, which is no longer on the plan."*
 * - `inputNotes[i]` — *"Adds up X, which is no longer on the plan."* for input `i`.
 */
export interface TotalVerdicts {
  errors: CompositeFormErrors;
  warnings: CompositeFormWarnings;
  targetNotes: string[];
  inputNotes: Array<string | undefined>;
}

export const totalVerdicts = (
  ctx: CompositeVerdictContext,
  composite: FloorPlannerComposite,
): TotalVerdicts => {
  const target = encodeRef(composite.target);
  const inputs = asArray(composite.inputs).map(encodeRef);

  /*
   * Labels for every reference this measure names, INCLUDING ones the plan no longer holds —
   * otherwise a message about a deleted entity renders a raw U+001F control byte. Exactly what
   * the editor builds for the measure in its form.
   *
   * ⚠ **The option map is built ONCE per plan change (`ctx.labels`), and copied only for a measure
   * that names something the plan lost.** It used to be rebuilt here for every measure — ~162
   * entries each, 1,458 insertions per recompute on staging plan 51, six times the cost of the
   * containment walk this pass exists to run — although its content is the same for all of them.
   */
  const lost = [target, ...inputs].filter(value => value !== '' && !ctx.labels.has(value));
  let labels: ReadonlyMap<string, string> = ctx.labels;
  if (lost.length > 0) {
    const extended = new Map(ctx.labels);
    for (const value of lost) {
      const ref = decodeRef(value);
      if (ref) extended.set(value, labelForRef(ref, ctx.options).label);
    }
    labels = extended;
  }

  /*
   * ⚠ The measure's OWN refid, not the empty string. `buildEdges` substitutes the candidate for
   * whatever the plan holds under that refid, and `findDuplicateTarget` skips itself by it — so
   * an empty refid would report every stored measure as a duplicate of itself.
   */
  const candidate = candidateOf(composite.refid, target === '' ? null : target, inputs);

  const formCtx: CompositeFormContext = {
    labels,
    pickable: ctx.pickable,
    observerCounts: ctx.observerCounts,
    codes: ctx.codes,
    graph: candidate ? ctx.graphOf(candidate) : undefined,
  };

  const form = {
    target: target === '' ? null : target,
    inputs,
    includesOwnObservers: composite.includesOwnObservers ?? false,
    excluded: composite.excluded ?? false,
  };

  /*
   * ⚠ **A reference the plan no longer holds is named HERE, and it used to be named only in a
   * browser console.** `labelForRef` resolves it to "no longer on the plan" rather than to a
   * raw encoded value, so the sentence says which measure and which reference.
   */
  const targetNotes: string[] = [];
  const inputNotes: Array<string | undefined> = inputs.map(() => undefined);
  const row = ctx.labelled.find(r => r.refid === composite.refid);
  if (row) {
    if (row.target.unresolved) {
      targetNotes.push(`Named for ${row.target.label}, which is no longer on the plan.`);
    }
    row.inputs.forEach((input, i) => {
      if (input.unresolved)
        inputNotes[i] = `Adds up ${input.label}, which is no longer on the plan.`;
    });
  }

  return {
    errors: validateCompositeForm(form, formCtx),
    warnings: warnCompositeForm(form, formCtx),
    targetNotes,
    inputNotes,
  };
};

/**
 * What is wrong with each measure the plan holds, in the words the editor uses for it.
 *
 * ⚠ **`totalVerdicts`, flattened.** A list-side check would be a second answer to one question,
 * and the two would eventually disagree about the same measure — which is the defect this whole
 * feature exists to prevent, one level up. So the list is the editor's verdict, rendered without
 * opening anything.
 *
 * It reports **every** refusal the twin would make at Go Live, not only the containment ones: a
 * list that stayed silent on a total genuinely blocking Go Live would be the disagreement above,
 * arrived at by omission. Rob's ruling, 2026-09-19.
 *
 * `problems` is "would be refused" — the set Launch holds Go live for. `warnings` is the second
 * channel, which blocks nothing.
 */
export const planCompositeVerdicts = (
  ctx: CompositeVerdictContext,
): {
  problems: Map<string, string[]>;
  warnings: Map<string, string[]>;
  /**
   * Live totals that add up nothing measured, all the way down — the ones held back from the edge
   * (`onboarding-polish-partial-composites`). From the same recursive rule Launch reads.
   */
  nothingMeasured: Set<string>;
} => {
  const byRefid = new Map<string, string[]>();
  /*
   * ⛔ **A SECOND MAP, not more entries in the first.** A warning is not a refusal: the twin files
   * these measures and goes live with them. The panel counts and words the two separately, and
   * merging them here would make its "would be refused" sentence false of half the list.
   */
  const warningsByRefid = new Map<string, string[]>();

  for (const composite of ctx.live) {
    const { errors, warnings, targetNotes, inputNotes } = totalVerdicts(ctx, composite);

    // Target, then the whole-list problem, then each row in row order. De-duplicated on the exact
    // sentence, because `duplicate-input` deliberately writes to a row AND to `members`.
    const sentences: string[] = [];
    for (const sentence of [errors.target, errors.members, ...errors.rows]) {
      if (sentence && sentences.indexOf(sentence) < 0) sentences.push(sentence);
    }
    if (sentences.length > 0) byRefid.set(composite.refid, sentences);

    /*
     * The same measure through the warnings channel. Nothing here blocks anything — the twin
     * files these and goes live with them — but a half-built total that said nothing at all
     * would be exactly the silent short number this feature removes. The "no longer on the plan"
     * notes follow, target first.
     */
    const notes: string[] = [];
    for (const sentence of [warnings.members, ...warnings.rows, ...targetNotes, ...inputNotes]) {
      if (sentence && notes.indexOf(sentence) < 0) notes.push(sentence);
    }

    if (notes.length > 0) warningsByRefid.set(composite.refid, notes);
  }

  const measured = computeCompleteness({
    observerCountsByRef: ctx.observerCounts,
    measures: ctx.others,
  });
  // A live total missing from the verdicts (an unencodable target) cannot be established as
  // measured, and "cannot establish" is not measured.
  const nothingMeasured = new Set(
    ctx.live.filter(c => measured.get(c.refid)?.measured !== true).map(c => c.refid),
  );

  return { problems: byRefid, warnings: warningsByRefid, nothingMeasured };
};

/**
 * The written total already standing on `target`, matched the way FILING matches a duplicate —
 * so the editor's "already totalled, opens it" and the duplicate refusal can never disagree about
 * whether two targets are one (`composites-page-editor`, spec R13). The first wins, as filing's
 * duplicate rule reads it. `except` leaves the total being edited out.
 */
export const writtenTotalOn = (
  ctx: CompositeVerdictContext,
  target: string,
  except?: string,
): FloorPlannerComposite | undefined =>
  target === ''
    ? undefined
    : ctx.live.find(
        c =>
          c.refid !== except &&
          encodeRef(c.target) !== '' &&
          ctx.sameTarget(encodeRef(c.target), target),
      );

/**
 * The picker's options as a TARGET may be chosen from: everything but equipment (ruled 2026-10-03,
 * maria@twinlabs.ai — *"they can only be a target of partial observers"*). A stored total that
 * already targets equipment keeps its own target in the list, so the field still shows it; nothing
 * is hidden (`composites-page-editor`, spec R6).
 */
export const targetOptions = (
  options: ComboboxOption[],
  current?: string | null,
): ComboboxOption[] => {
  const isEquipment = (value: string) => {
    const ref = decodeRef(value);
    return ref?.kind === 'entity' && ref.entityKind === 'item';
  };
  const out = options.filter(o => !isEquipment(o.value));
  if (current && isEquipment(current)) {
    const kept = options.find(o => o.value === current);
    if (kept) out.push(kept);
  }
  return out;
};

/** What a reference totals, in the list's words. An entity is a zone (surface) or equipment (item). */
export const targetKindOf = (
  ref: FloorPlannerCompositeRef | null | undefined,
): CompositeTargetKind => {
  if (!ref || ref.kind === 'twin') return 'twin';
  if (ref.kind === 'floor') return 'floor';
  return ref.entityKind === 'item' ? 'equipment' : 'zone';
};

const counted = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * **Where each source of each total stands** (`composites-page-list`, spec R11) — what its chip
 * shows on the list now, and what the editor's source cards will read.
 *
 * ⚠ **No rule of its own.** A source whose target has a written total takes the state that total's
 * own row shows — `problems` → refused, `warnings` → not finished, a review → under review, else
 * complete — from the SAME verdicts the rows read, in the list's own precedence (`rowState`,
 * pinned against it in `compositeVerdicts.sourceStates.test.ts`). **One deliberate overlay:** an
 * excluded total that is otherwise complete or under review reads *excluded* on its chips (spec
 * R10's grey dot), while its own row keeps its state and shows *Not published*. A second judgement here
 * would let a chip say "complete" beside a row saying "would be refused", which is the
 * disagreement this module exists to prevent. Without a written total: the automatic total its
 * observers make, else its own observer, else nothing at all.
 *
 * Keyed by the row's refid; entries are in chip order, with the target's own cameras first when
 * the total counts them. `rowRefid` names the source's own row (a written total on that target, or
 * its automatic row, `automatic:{encoded}` as the host keys it); absent, the chip has nowhere to
 * jump and renders static.
 */
/** What `sourceStateOf` reads, built once per plan for every reference it is asked about. */
export interface SourceLookups {
  /** The written total on each encoded target. The first wins, as filing's duplicate rule reads it. */
  writtenOn: ReadonlyMap<string, FloorPlannerComposite>;
  /** The automatic total on each encoded target a written total does not already stand on. */
  automaticOn: ReadonlyMap<string, AutomaticTotal>;
  stateOfWritten: (c: FloorPlannerComposite) => [CompositeSourceState, string];
}

export const sourceLookups = (
  ctx: CompositeVerdictContext,
  verdicts: ReturnType<typeof planCompositeVerdicts>,
  {
    automatic,
    reviews,
  }: {
    automatic: ReadonlyArray<AutomaticTotal>;
    reviews: ReadonlyMap<string, string[]>;
  },
): SourceLookups => {
  const writtenOn = new Map<string, FloorPlannerComposite>();
  for (const c of ctx.live) {
    const key = encodeRef(c.target);
    if (key !== '' && !writtenOn.has(key)) writtenOn.set(key, c);
  }
  const automaticOn = new Map<string, AutomaticTotal>();
  for (const total of automatic) {
    const key = encodeRef(total.target);
    if (key !== '' && !writtenOn.has(key)) automaticOn.set(key, total);
  }

  const stateOfWritten = (c: FloorPlannerComposite): [CompositeSourceState, string] => {
    if ((verdicts.problems.get(c.refid) ?? []).length > 0)
      return ['refused', 'Its total would be refused'];
    // Split by what reaches the live system (Rob, 2026-10-07): the dot follows it, and so do the
    // words, because the chip's accessible name is its label and these words.
    // An excluded total is published nowhere, so "goes live" would be false of it (Rob, 2026-10-07).
    if ((verdicts.warnings.get(c.refid) ?? []).length > 0 && c.excluded)
      return ['excluded', 'Its total is excluded'];
    if ((verdicts.warnings.get(c.refid) ?? []).length > 0)
      return verdicts.nothingMeasured.has(c.refid)
        ? ['unfinished', 'Its total is not finished, and has nothing measured']
        : ['partial', 'Its total is not finished, and goes live with what it has'];
    if (c.excluded) return ['excluded', 'Its total is excluded'];
    if ((reviews.get(c.refid) ?? []).length > 0) return ['review', 'Its total is under review'];
    return ['complete', 'Complete'];
  };
  return { writtenOn, automaticOn, stateOfWritten };
};

/**
 * **Where ONE reference stands as a source** — the precedence `sourceStates` gives every input of
 * every total, asked about any reference. The *Add source* picker asks it about each candidate, so
 * a source reads the same in the list it is picked from as on the card it becomes
 * (`composites-page-picker`, spec R6). One function, two callers: two copies could drift.
 */
export const sourceStateOf = (
  ctx: CompositeVerdictContext,
  { writtenOn, automaticOn, stateOfWritten }: SourceLookups,
  input: FloorPlannerCompositeRef,
): Pick<CompositeSource, 'state' | 'text' | 'rowRefid'> => {
  const key = encodeRef(input);
  const { unresolved } = labelForRef(input, ctx.options);
  if (unresolved || key === '') return { state: 'gone', text: 'No longer on the plan' };
  const written = writtenOn.get(key);
  if (written) {
    const [state, text] = stateOfWritten(written);
    return { state, text, rowRefid: written.refid };
  }
  const auto = automaticOn.get(key);
  if (auto) {
    return {
      state: 'automatic',
      text: `Automatic · ${counted(auto.cameras, 'observer', 'observers')}`,
      rowRefid: automaticRowRefid(key),
    };
  }
  const observers = ctx.observerCounts.get(key) ?? 0;
  return observers > 0
    ? { state: 'observed', text: counted(observers, 'observer', 'observers') }
    : { state: 'uncovered', text: 'No camera, no total' };
};

export const sourceStates = (
  ctx: CompositeVerdictContext,
  verdicts: ReturnType<typeof planCompositeVerdicts>,
  {
    automatic,
    reviews,
  }: {
    automatic: ReadonlyArray<AutomaticTotal>;
    reviews: ReadonlyMap<string, string[]>;
  },
  /** Built once by a caller that also needs them (the *Add source* picker); else built here. */
  lookups: SourceLookups = sourceLookups(ctx, verdicts, { automatic, reviews }),
): Map<string, CompositeSource[]> => {
  const out = new Map<string, CompositeSource[]>();
  for (const c of ctx.live) {
    const sources: CompositeSource[] = [];
    if (c.includesOwnObservers) {
      const n = ctx.observerCounts.get(encodeRef(c.target)) ?? 0;
      sources.push({
        key: 'own-cameras',
        label: 'Its own cameras',
        kind: targetKindOf(c.target),
        // A total can keep counting its own cameras after the last one has gone - the
        // completeness rule calls that unfinished, so the chip says no data (Copilot, PR #1914).
        state: n > 0 ? 'own-cameras' : 'uncovered',
        text: counted(n, 'camera', 'cameras'),
      });
    }
    asArray(c.inputs).forEach((input, at) => {
      const key = encodeRef(input);
      const { label } = labelForRef(input, ctx.options);
      sources.push({
        key: `${at}:${key}`,
        label,
        kind: targetKindOf(input),
        ...sourceStateOf(ctx, lookups, input),
      });
    });
    out.set(c.refid, sources);
  }
  return out;
};

/* ------------------------------------------------------------------------------------------------
 * The editor's two pickers (`composites-page-picker`).
 * --------------------------------------------------------------------------------------------- */

/**
 * What picking `value` in the *Total for* field does — **the one decision the field's groups and
 * the pick itself both read**, so a section label can never say "opens it" for a pick that
 * replaces (spec R1). Lifted unchanged from `pickTarget` (`composites-page-editor`, R13/R14 and
 * amendment 6):
 *
 * - `opens` — a live written total stands on that target, matched the way filing matches it
 *   (`writtenTotalOn`), other than the session's own; picking it moves the panel to it.
 * - `replaces` — re-targeting an existing total (`sessionRefid` set) onto the EXACT target of a
 *   generated total while the page is generating (the reconciler then drops the generated one),
 *   or a target only an automatic total stands on (filing drops the automatic one).
 * - `same` — the session's own target: picking it changes nothing.
 * - `new` — nothing totals it.
 *
 * `live` is read by the caller: the render's for the groups, the store's at pick time for the pick.
 */
export type TargetPickOutcome = 'new' | 'replaces' | 'opens' | 'same';

export const targetPickOutcome = (
  ctx: CompositeVerdictContext,
  value: string,
  {
    live,
    sessionRefid,
    currentTarget,
    generating,
    automatic,
  }: {
    live: FloorPlannerComposite[];
    sessionRefid?: string | null;
    currentTarget?: string | null;
    generating: boolean;
    automatic: ReadonlyArray<AutomaticTotal>;
  },
): { outcome: TargetPickOutcome; found?: FloorPlannerComposite; auto?: AutomaticTotal } => {
  const found = writtenTotalOn({ ...ctx, live }, value, sessionRefid ?? undefined);
  /*
   * Exact target only: a total on a target that merely folds to the same filing key (two floors
   * named alike) is not one the page would drop, so it opens rather than being replaced. And only
   * while the page is generating: it is the reconciler that drops the generated total, so while
   * the configuration is loading or unreadable it stays (Copilot, PR #1928).
   */
  const replaceable =
    !!found &&
    !!sessionRefid &&
    generating &&
    found.generated === true &&
    encodeRef(found.target) === value;
  /*
   * A written total found on the session's OWN target is another total standing on it (a
   * duplicate, or one folding to the same filing key): picking it opens that one, so it lists as
   * `opens` rather than `same` — the section says what the pick does (spec amendment 8).
   */
  if (found && !replaceable) return { outcome: 'opens', found };
  if (found) return { outcome: 'replaces', found };
  if (currentTarget && value === currentTarget) return { outcome: 'same' };
  const auto = automatic.find(t => encodeRef(t.target) === value);
  if (auto) return { outcome: 'replaces', auto };
  return { outcome: 'new' };
};

/**
 * Would adding `key` as a source make `total` add up itself? The rules' own `findCycle`, asked
 * about the one new input — so the picker leaves out exactly the sources the cycle refusal would
 * refuse (ruled 2026-10-06, maria@twinlabs.ai; spec R7). Only the new edge is asked: a total
 * that already loops through an existing input does not hide unrelated candidates.
 */
export const wouldLoop = (
  ctx: CompositeVerdictContext,
  total: Pick<FloorPlannerComposite, 'refid' | 'target'>,
  key: string,
): boolean => {
  const target = encodeRef(total.target);
  if (!target || !key || key === target) return false;
  const candidate = candidateOf(total.refid, target, [key]);
  return candidate !== null && findCycle(candidate, ctx.others) === key;
};

/**
 * Picker order (spec R4/R8): the whole twin first, then names in natural order (*Zone 2* before
 * *Zone 11*) with the onboarding wizard's one collator, then the code, then the value — a total
 * order, so a re-render never reshuffles equal names.
 */
export const byPickerOrder = (a: PickerCandidate, b: PickerCandidate): number => {
  const twin = Number(b.kind === 'twin') - Number(a.kind === 'twin');
  if (twin !== 0) return twin;
  return (
    NAME_COLLATOR.compare(a.label, b.label) ||
    NAME_COLLATOR.compare(a.code ?? '', b.code ?? '') ||
    (a.value < b.value ? -1 : a.value > b.value ? 1 : 0)
  );
};

/** Sections in their order, each sorted; ungrouped (the session's own target) first. */
const inSections = (
  candidates: PickerCandidate[],
  order: ReadonlyArray<string>,
): PickerCandidate[] => [
  ...candidates.filter(c => c.group === undefined).sort(byPickerOrder),
  ...order.flatMap(group => candidates.filter(c => c.group === group).sort(byPickerOrder)),
];

/**
 * The *Total for* field's options (spec R1–R4): everything but equipment (`targetOptions`), each in
 * the section `targetPickOutcome` puts it in, with its state in the words the list uses for it.
 * `stateOfTotal` is the host's row state for a written total, so an option and that total's row
 * cannot disagree.
 */
export const targetPickerCandidates = (
  ctx: CompositeVerdictContext,
  {
    automatic,
    stateOfTotal,
    sessionRefid,
    currentTarget,
    generating,
  }: {
    automatic: ReadonlyArray<AutomaticTotal>;
    stateOfTotal: (refid: string) => CompositeRowState | undefined;
    sessionRefid?: string | null;
    currentTarget?: string | null;
    generating: boolean;
  },
): PickerCandidate[] => {
  /*
   * A written total's words are its own row's chip (`stateOfTotal`, from the same render's rows,
   * which list every live total — so the fallback is not reached from the host). The dot is the
   * source chip's own split (`dataFor`): one table for every data dot.
   */
  const ofTotal = (refid: string | null | undefined): Pick<PickerCandidate, 'state' | 'text'> => {
    const state = refid ? stateOfTotal(refid) : undefined;
    return state
      ? { state: dataFor(state), text: STATE_CHIP[state].text }
      : { state: 'has', text: STATE_CHIP.complete.text };
  };
  const out: PickerCandidate[] = targetOptions(ctx.options, currentTarget).map(option => {
    const ref = decodeRef(option.value);
    const base = {
      value: option.value,
      label: option.label,
      code: option.meta,
      kind: targetKindOf(ref),
    };
    const { outcome, found, auto } = targetPickOutcome(ctx, option.value, {
      live: ctx.live,
      sessionRefid,
      currentTarget,
      generating,
      automatic,
    });
    switch (outcome) {
      case 'same':
        return { ...base, ...ofTotal(sessionRefid) };
      case 'opens':
        return { ...base, group: PICKER_GROUP.opens, ...ofTotal(found?.refid) };
      case 'replaces': {
        if (found) return { ...base, group: PICKER_GROUP.replaces, ...ofTotal(found.refid) };
        return {
          ...base,
          group: PICKER_GROUP.replaces,
          state: 'has',
          text: counted(auto?.cameras ?? 0, 'observer', 'observers'),
        };
      }
      default: {
        const observers = ctx.observerCounts.get(option.value) ?? 0;
        return {
          ...base,
          group: PICKER_GROUP.new,
          ...(observers > 0
            ? { state: 'has' as const, text: counted(observers, 'observer', 'observers') }
            : { state: 'none' as const, text: 'No camera' }),
        };
      }
    }
  });
  return inSections(out, [PICKER_GROUP.new, PICKER_GROUP.replaces, PICKER_GROUP.opens]);
};

/**
 * The *Add source* picker's options (spec R6–R8): everything on the plan but the target, what the
 * total already adds up, and anything that would make it add up itself — each in the state its
 * card will carry once added (`sourceStateOf`), under *Has data* or *No data yet* by the source
 * chip's own split (`dataFor`).
 */
export const sourcePickerCandidates = (
  ctx: CompositeVerdictContext,
  total: Pick<FloorPlannerComposite, 'refid' | 'target' | 'inputs'>,
  lookups: SourceLookups,
): PickerCandidate[] => {
  const target = encodeRef(total.target);
  const inTotal = new Set(asArray(total.inputs).map(encodeRef));
  const out: PickerCandidate[] = [];
  for (const option of ctx.options) {
    if (option.value === target || inTotal.has(option.value)) continue;
    if (wouldLoop(ctx, total, option.value)) continue;
    const ref = decodeRef(option.value);
    if (!ref) continue;
    const { state, text } = sourceStateOf(ctx, lookups, ref);
    const data = dataFor(state);
    out.push({
      value: option.value,
      label: option.label,
      code: option.meta,
      kind: targetKindOf(ref),
      group: data === 'has' ? PICKER_GROUP.has : PICKER_GROUP.none,
      state: data,
      text,
    });
  }
  return inSections(out, [PICKER_GROUP.has, PICKER_GROUP.none]);
};
